// In-Browser-Demo-API für den Demo-Mode (siehe frontend/src/lib/env.ts).
//
// Im Demo-Mode leitet `fetchJson()` jede Anfrage hierher um. Statt das echte
// Backend anzusprechen, beantworten wir die real genutzten Endpunkte aus einem
// anonymisierten oder synthetisierten Datensatz-Bundle
// (`/demo/dataset.json`). Das Bundle wird von `scripts/build_demo_dataset.py`
// erzeugt und leak-gegatet; siehe docs/marketing_demo_sync.md.
//
// Bewusst read-only und auf die demonstrierten Feature-Bereiche begrenzt
// (Cockpit, Belege, Angebote inkl. Angebotsprüfung, Artikel + Detail,
// Kostenstellen, Preis-Analyse). Endpunkte ausserhalb des Scopes liefern
// sinnvolle Defaults oder einen 404.

import { ApiError } from "../lib/api";
import {
  SyntheticCreditRangeError,
  synthesizeCreditRange,
} from "./creditRangeModel";

type Json = Record<string, unknown>;

let datasetPromise: Promise<DemoDataset> | null = null;
const PRICE_ANOMALY_DELTA_THRESHOLD = 15;

type DemoDataset = {
  version?: string;
  generated_at?: string;
  auth?: Json;
  cockpit?: {
    summary?: Json;
    spend_monthly?: Json;
    overpay_monthly?: Json;
  };
  belege?: {
    items?: Json[];
    detail?: Record<string, Json>;
    credits?: Json;
    credit_range_model?: Json;
  };
  angebote?: { items?: Json[]; detail?: Record<string, Json> };
  articles?: {
    all?: Json[];
    activity_period?: Json;
    abc_summary?: Json;
    wholesale_suppliers?: Json;
    wholesale_supplier_rollup?: Json;
    detail?: Record<string, Json>;
  };
  manufacturers?: Json;
  projects?: { items?: Json[]; summary?: Json };
  cost_centers?: {
    items?: Json[];
    source_coverage?: Json;
    manual_events?: Json;
  };
  variance?: {
    flat?: Json[];
    grouped?: Record<string, Json>;
    users?: Json;
  };
  price_anomaly_rule?: Json;
  product_opportunities?: Json;
};

async function loadDataset(): Promise<DemoDataset> {
  if (!datasetPromise) {
    datasetPromise = fetch("/demo/dataset.json", {
      headers: { Accept: "application/json" },
    }).then((res) => {
      if (!res.ok) {
        throw new ApiError(res.status, "Demo-Datensatz konnte nicht geladen werden.");
      }
      return res.json() as Promise<DemoDataset>;
    });
  }
  return datasetPromise;
}

// Read-only Lookups, die aus Vertragsgründen POST sind (Body = Code-Liste).
// Sie ändern nichts und dürfen deshalb im Demo-Modus beantwortet werden.
const DEMO_POST_LOOKUP_PATHS = new Set([
  "/api/articles/resolve",
  "/api/articles/reference-prices",
  "/api/purchasing-target/references",
]);

export async function demoFetchJson<T>(
  input: RequestInfo,
  init?: RequestInit,
): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  const rawUrl = typeof input === "string" ? input : (input as Request).url;
  const url = new URL(rawUrl, "http://demo.local");
  const path = url.pathname;
  const params = url.searchParams;

  if (method !== "GET" && !DEMO_POST_LOOKUP_PATHS.has(path)) {
    throw new ApiError(403, "Im Demo-Modus sind Änderungen deaktiviert.");
  }

  let body: Json | undefined;
  if (method !== "GET" && typeof init?.body === "string") {
    try {
      body = JSON.parse(init.body) as Json;
    } catch {
      throw new ApiError(400, "Demo: ungültiger Anfrage-Body.");
    }
  }

  const dataset = await loadDataset();
  const result = route(path, params, dataset, body);
  if (result === undefined) {
    throw new ApiError(404, `Demo: kein Datensatz für ${path}.`);
  }
  return result as unknown as T;
}

function route(
  path: string,
  params: URLSearchParams,
  d: DemoDataset,
  body?: Json,
): unknown {
  // --- Auth ---
  if (path === "/api/auth/me" || path === "/api/auth/profile") {
    return d.auth ?? null;
  }

  // --- Cockpit ---
  if (path === "/api/cockpit/summary") return d.cockpit?.summary ?? {};
  if (path === "/api/cockpit/spend-monthly") {
    return d.cockpit?.spend_monthly ?? { months: 0, items: [] };
  }
  if (path === "/api/cockpit/overpay-monthly") {
    return d.cockpit?.overpay_monthly ?? { months: 0, items: [] };
  }
  // --- Belege ---
  // Credit aggregates cannot be reconstructed from `belege.items`: that list
  // is a deliberately curated Demo subset, not the tenant-wide evidence
  // stock. Older bundles therefore fail explicitly instead of presenting a
  // misleading zero balance.
  if (path === "/api/belege/credits") {
    return creditDashboard(params, d);
  }
  if (path === "/api/belege/supplier-options") {
    return { items: [...(d.belege?.items ?? [])]
      .sort(belegComparator("amount_desc"))
      .slice(0, 1000)
      .map(item => ({ supplier: item.supplier, total_amount: item.total_amount })) };
  }
  if (path === "/api/belege") return belegeList(params, d);
  const belegDetailMatch = matchPath(path, "/api/belege/:id");
  if (belegDetailMatch) return belegDetail(belegDetailMatch.id, d);

  // --- Angebote ---
  if (path === "/api/angebote") return angeboteList(params, d);
  const angebotComparisonMatch = matchPath(path, "/api/angebote/:id/comparison");
  if (angebotComparisonMatch) {
    const detail = angebotDetail(angebotComparisonMatch.id, d);
    if (!detail || !isJson(detail.angebot) || !Array.isArray(detail.lines)) return undefined;
    const offer = detail.angebot;
    return { ...offer, reference_date: offer.doc_date, reference_basis: params.get("basis") ?? "last", warnings: [], save_allowed: false,
      lines: detail.lines.filter(isJson).map(line => ({ ...line, reference: null, comparison_key: null, comparison_warning: "Keine sichere Referenz in diesen Beispieldaten" })) };
  }
  const angebotDetailMatch = matchPath(path, "/api/angebote/:id");
  if (angebotDetailMatch) return angebotDetail(angebotDetailMatch.id, d);

  // --- Articles (collection) ---
  if (path === "/api/articles") return articleList(params, d);
  if (path === "/api/articles/filter-options") return articleFilterOptions(params, d);
  if (path === "/api/articles/suggest") return articleSuggest(params, d);
  if (path === "/api/articles/abc-summary") {
    return d.articles?.abc_summary ?? { items: [] };
  }
  if (path === "/api/articles/wholesale-suppliers") {
    return d.articles?.wholesale_suppliers ?? { items: [] };
  }
  if (path === "/api/articles/wholesale-supplier-rollup") {
    return d.articles?.wholesale_supplier_rollup ?? { items: [] };
  }
  if (path === "/api/articles/resolve") {
    return articleResolve(body, d);
  }
  if (path === "/api/purchasing-target") {
    // Demo has no customer company policy; writes remain forbidden above.
    return { current: null, history: [], can_manage: false };
  }
  if (path === "/api/purchasing-target/references") {
    const keys = Array.isArray(body?.article_group_keys) ? body.article_group_keys as string[] : [];
    const basis = body?.basis === "best" ? "best" : "last";
    const articles = (d.articles?.all ?? []).filter(article => keys.includes(String(article.article_group_key)));
    // Aggregated demo history cannot prove canonical unit/source eligibility.
    // Keep the gap explicit rather than imply that a rollup is an exact match.
    return { basis: `purchasing_${basis}`, mode: basis, window_months: 12, reference_date: body?.as_of,
      items: articles.map(article => ({ ...article, reference_price: null, reference_doc_date: null,
        reference_invoice_doc_id: null, reference_doc_number: null, reference_supplier: null,
        reference_uom: null, reference_source_kind: null, priced_line_count_window: 0,
        reference_warning: "Die Demo enthält für diese Preisanfrage keinen vollständigen Rechnungsnachweis." })),
      missing_article_group_keys: keys.filter(key => !articles.some(article => article.article_group_key === key)) };
  }
  if (path === "/api/articles/reference-prices") {
    return articleReferencePrices(body, d);
  }

  // --- Articles (detail subresources) ---
  const articleSub = matchPath(path, "/api/articles/:key/:sub");
  if (articleSub) {
    return articleDetail(articleSub.key, articleSub.sub, d);
  }
  const article = matchPath(path, "/api/articles/:key");
  if (article) {
    return articleDetail(article.key, "article", d);
  }

  // --- Manufacturers ---
  if (path === "/api/manufacturers") {
    return d.manufacturers ?? { limit: 0, offset: 0, items: [] };
  }

  // --- Cost centers ---
  if (path === "/api/cost-centers") return costCenterList(params, d);
  if (path === "/api/cost-centers/source-coverage/detail") {
    return costCenterCoverageDetail(params, d);
  }
  if (path === "/api/cost-centers/source-coverage") {
    return costCenterCoverage(params, d);
  }
  if (path === "/api/cost-centers/period-spend") {
    const entity = costCenterDetail(params, d);
    if (!entity) return undefined;
    const window = params.get("window") ?? "all";
    const field = ({all:"total_spend",last_year:"spend_last_year",ytd:"spend_ytd",last30:"spend_last30"} as Record<string,string>)[window];
    return {demo_limited:true, matching_count:0, from_date:null, summary:{total_spend:entity[field] ?? null,credit_spend:window==="all"?entity.credit_spend:window==="last_year"?entity.credit_spend_last_year:null,document_count:null,line_count:null,article_count:null,undated_count:0,missing_amount_count:0},months:[],items:[],articles:[],suppliers:[]};
  }
  if (path === "/api/cost-centers/detail") {
    return costCenterDetail(params, d);
  }
  if (path === "/api/cost-centers/manual-events") {
    return d.cost_centers?.manual_events ?? { items: [] };
  }
  const spendByMonth = matchPath(
    path,
    "/api/cost-centers/:type/:key/spend-by-month",
  );
  if (spendByMonth) return costCenterSpendByMonth(spendByMonth.key, d);

  // --- Projects ---
  if (path === "/api/projects") return projectList(params, d);
  const projectMatch = matchPath(path, "/api/projects/:key");
  if (projectMatch) {
    return (d.projects?.items ?? []).find(
      (p) => p.project_key === projectMatch.key,
    );
  }

  // --- Reports / Preis-Analyse ---
  if (path === "/api/price-anomaly-rule") {
    return d.price_anomaly_rule ?? demoPriceAnomalyRule();
  }
  if (path === "/api/reports/price-variance") return varianceFlat(params, d);
  if (path === "/api/reports/price-variance/grouped") {
    return varianceGrouped(params, d);
  }
  if (path === "/api/reports/purchases/monthly") return purchasesMonthly(params, d);
  if (path === "/api/reports/users/variance") {
    return d.variance?.users ?? { total: 0, limit: 0, offset: 0, items: [] };
  }
  if (path === "/api/reports/product-opportunities") {
    return (
      d.product_opportunities ?? {
        total: 0,
        limit: 0,
        offset: 0,
        sort: "gap_desc",
        identity_sources: null,
        price_basis: null,
        min_gap_pct: 0,
        items: [],
      }
    );
  }

  return undefined;
}

// ---------------------------------------------------------------------------
// Endpoint-Implementierungen
// ---------------------------------------------------------------------------

const CREDIT_CUSTOM_MAX_DAYS = 3660;

function creditDashboard(
  params: URLSearchParams,
  d: DemoDataset,
): Json | undefined {
  const credits = d.belege?.credits;
  if (!credits) return undefined;
  const from = params.get("from");
  const to = params.get("to");
  if (from === null && to === null) return credits;
  if (!from || !to) {
    throw new ApiError(
      400,
      "Von- und Bis-Datum müssen gemeinsam angegeben werden.",
    );
  }

  const fromDay = creditIsoDateDay(from);
  const toDay = creditIsoDateDay(to);
  const asOf = credits.as_of_date;
  const asOfDay =
    typeof asOf === "string" ? creditIsoDateDay(asOf) : null;
  if (fromDay === null || toDay === null || asOfDay === null) {
    throw new ApiError(422, "Demo: ungültiger Gutschriften-Zeitraum.");
  }
  if (fromDay > toDay) {
    throw new ApiError(
      400,
      "Das Von-Datum darf nicht nach dem Bis-Datum liegen.",
    );
  }
  if (toDay > asOfDay) {
    throw new ApiError(400, "Das Bis-Datum darf nicht in der Zukunft liegen.");
  }
  const inclusiveDays = Math.round((toDay - fromDay) / 86_400_000) + 1;
  if (inclusiveDays > CREDIT_CUSTOM_MAX_DAYS) {
    throw new ApiError(
      400,
      "Der eigene Zeitraum darf höchstens 3.660 Tage umfassen.",
    );
  }

  const model = d.belege?.credit_range_model;
  if (!model) {
    throw new ApiError(
      404,
      "Demo: eigene Gutschriften-Zeiträume sind nicht verfügbar.",
    );
  }
  let custom: Json;
  try {
    custom = synthesizeCreditRange(credits, model, from, to);
  } catch (error) {
    if (error instanceof SyntheticCreditRangeError) {
      throw new ApiError(500, error.message);
    }
    throw error;
  }
  const periods = credits.periods;
  if (!isJson(periods)) {
    throw new ApiError(500, "Demo: periods ist nicht lesbar.");
  }
  return {
    ...credits,
    periods: {
      ...periods,
      custom,
    },
  };
}

function creditIsoDateDay(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const timestamp = Date.UTC(year, month - 1, day);
  const parsed = new Date(timestamp);
  return parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
    ? timestamp
    : null;
}

function belegeList(params: URLSearchParams, d: DemoDataset): Json {
  let items = (d.belege?.items ?? []).map((item) =>
    withBelegReviewCounts(item, d),
  );
  const reviewStatus = params.get("review_status");
  if (reviewStatus) {
    items = items.filter((b) => b.review_status === reviewStatus);
  }
  const reviewState = params.get("review_state");
  if (reviewState) {
    const countKey = REVIEW_STATE_COUNT_KEYS[reviewState];
    items = countKey ? items.filter((b) => num(b[countKey]) > 0) : [];
  }
  if (params.get("with_comments") === "true") {
    items = items.filter((b) => num(b.review_comment_count) > 0);
  }
  const docType = params.get("doc_type");
  if (docType) {
    items = items.filter((b) => b.doc_type === docType);
  }
  const from = params.get("from");
  const to = params.get("to");
  if (from || to) items = items.filter(b => typeof b.doc_date === "string" && (!from || b.doc_date >= from) && (!to || b.doc_date <= to));
  const sort = params.get("sort") ?? "recent_desc";
  items.sort(belegComparator(sort));
  const limit = intParam(params, "limit", 120);
  const offset = intParam(params, "offset", 0);
  const page = items.slice(offset, offset + limit);
  return {
    total: items.length,
    limit,
    offset,
    sort,
    summary: belegeSummary(items),
    items: page,
  };
}

// Die Demo ist bewusst synthetisch: Wie bei den Beleg-Positionen entstehen
// die Angebote deterministisch aus den Demo-Artikeln, damit die
// Angebotsprüfung ohne Bundle-Refresh echte Referenzpreise, Über-/Im-Ziel-
// Status und sichtbare Lücken zeigt. Liefert das Bundle ein eigenes
// `angebote`-Segment (siehe scripts/build_demo_dataset.py), hat es Vorrang.
let angeboteCache: { items: Json[]; detail: Record<string, Json> } | null =
  null;

function demoAngebote(d: DemoDataset): {
  items: Json[];
  detail: Record<string, Json>;
} {
  const bundled = d.angebote?.items;
  if (bundled && bundled.length > 0) {
    return { items: bundled, detail: d.angebote?.detail ?? {} };
  }
  if (angeboteCache) return angeboteCache;

  const articles = d.articles?.all ?? [];
  const baseDate = String(d.generated_at ?? "2026-06-01").slice(0, 10);
  const round2 = (n: number) => Math.round(n * 100) / 100;

  // Nur Artikel mit auflösbarem Referenzpreis kommen in den Pool; die Specs
  // greifen modulo Poolgröße zu, damit die Synthese auch bei kleinen
  // Demo-Bundles (aktuell 8 Artikel) drei Angebote liefert.
  const pool = articles
    .map((article) => ({
      article,
      key: String(article.article_group_key ?? ""),
      reference: demoReferenceNet(String(article.article_group_key ?? ""), d),
    }))
    .filter(
      (entry): entry is typeof entry & { reference: number } =>
        entry.key.length > 0 && entry.reference !== null,
    );
  if (pool.length === 0) {
    angeboteCache = { items: [], detail: {} };
    return angeboteCache;
  }

  const specs = [
    {
      id: "angebot:demo-1",
      docNumber: "AN-2026-114",
      supplier: "SHT",
      daysAgo: 2,
      pick: [0, 1, 2, 3, 4],
      overIdx: new Set([0, 3]),
      gapLines: 1,
    },
    {
      id: "angebot:demo-2",
      docNumber: "AN-2026-109",
      supplier: "GC",
      daysAgo: 6,
      pick: [5, 6, 7, 1],
      overIdx: new Set([1]),
      gapLines: 1,
    },
    {
      id: "angebot:demo-3",
      docNumber: "AN-2026-101",
      supplier: "SHT",
      daysAgo: 13,
      pick: [2, 4, 6],
      overIdx: new Set([0, 2]),
      gapLines: 0,
    },
  ];

  const items: Json[] = [];
  const detail: Record<string, Json> = {};
  for (const spec of specs) {
    const lines: Json[] = [];
    const usedKeys = new Set<string>();
    for (const [idx, poolIdx] of spec.pick.entries()) {
      const entry = pool[poolIdx % pool.length];
      if (!entry || usedKeys.has(entry.key)) continue;
      usedKeys.add(entry.key);
      const { article, key, reference } = entry;
      // Über Zielpreis: deutlich über dem besten 12-Monats-Preis; im Ziel:
      // knapp darunter — so bleibt der Zeilenstatus unabhängig von den
      // konkreten Bundle-Preisen stabil.
      const factor = spec.overIdx.has(idx)
        ? 1.1 + (idx % 3) * 0.04
        : 0.95 + (idx % 2) * 0.02;
      const net = round2(reference * factor);
      const qty = ((idx * 2 + 3) % 8) + 1;
      const discount = num(article.avg_discount_pct);
      const list =
        discount > 0 && discount < 100
          ? round2(net / (1 - discount / 100))
          : net;
      lines.push({
        line_pos: String(lines.length + 1),
        page_no: 1 + Math.floor(lines.length / 6),
        article_code: article.primary_article_code ?? null,
        alt_article_code: null,
        manufacturer_article_code: null,
        description: article.description_example ?? null,
        qty,
        uom: "ST",
        list_unit_price: list,
        discount_pct: discount > 0 && discount < 100 ? round2(discount) : null,
        net_unit_price: net,
        line_net_amount: round2(net * qty),
        article_group_key: key,
        match_status: "matched",
        article_detail_available: true,
      });
    }
    for (let gap = 0; gap < spec.gapLines; gap += 1) {
      const net = 4.9 + gap * 2.4;
      lines.push({
        line_pos: String(lines.length + 1),
        page_no: 1 + Math.floor(lines.length / 6),
        article_code: `99901${gap + 5}`,
        alt_article_code: null,
        manufacturer_article_code: null,
        description: "Kleinmaterial und Befestigung (Set)",
        qty: 2,
        uom: "ST",
        list_unit_price: null,
        discount_pct: null,
        net_unit_price: round2(net),
        line_net_amount: round2(net * 2),
        article_group_key: null,
        match_status: "unresolved",
        article_detail_available: false,
      });
    }
    if (lines.length < 3) continue;
    const positionsTotal = round2(
      lines.reduce((sum, line) => sum + num(line.line_net_amount), 0),
    );
    const row: Json = {
      angebot_id: spec.id,
      doc_number: spec.docNumber,
      doc_date: dateMinusDays(baseDate, spec.daysAgo),
      supplier: spec.supplier,
      currency: "EUR",
      page_count: Math.max(...lines.map((line) => num(line.page_no))),
      line_count: lines.length,
      priced_line_count: lines.length,
      positions_total: positionsTotal,
      pdf_available: false,
    };
    items.push(row);
    detail[spec.id] = { angebot: row, line_total: lines.length, lines };
  }

  angeboteCache = { items, detail };
  return angeboteCache;
}

// Bester 12-Monats-Preis eines Demo-Artikels — dieselbe Auflösung wie
// `articleReferencePrices` (Detail-Historie, sonst 12-Monats-Rollup).
function demoReferenceNet(key: string, d: DemoDataset): number | null {
  const article = (d.articles?.all ?? []).find(
    (a) => a.article_group_key === key,
  );
  if (!article) return null;
  const detail = d.articles?.detail?.[key] as
    | { price_history?: { items?: Json[] } }
    | undefined;
  const history = detail?.price_history?.items;
  const cutoffDate = new Date();
  cutoffDate.setMonth(cutoffDate.getMonth() - 12);
  const cutoff = cutoffDate.toISOString().slice(0, 10);
  let best: number | null = null;
  if (Array.isArray(history)) {
    for (const row of history) {
      const net = num(row.net_unit_price);
      if (
        net > 0 &&
        String(row.doc_date ?? "") >= cutoff &&
        (best === null || net < best)
      ) {
        best = net;
      }
    }
  }
  if (best === null && article.min_net_unit_price_12m != null) {
    best = num(article.min_net_unit_price_12m);
  }
  return best !== null && best > 0 ? best : null;
}

function dateMinusDays(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
}

function angeboteList(params: URLSearchParams, d: DemoDataset): Json {
  let items = [...demoAngebote(d).items];
  const q = (params.get("q") ?? "").trim().toLowerCase();
  if (q) {
    items = items.filter((angebot) =>
      [angebot.doc_number, angebot.supplier].some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(q),
      ),
    );
  }
  const supplier = params.get("supplier");
  if (supplier) {
    items = items.filter((angebot) => angebot.supplier === supplier);
  }
  const sort = params.get("sort") ?? "recent_desc";
  items.sort((a, b) =>
    sort === "amount_desc"
      ? num(b.positions_total) - num(a.positions_total)
      : String(b.doc_date ?? "").localeCompare(String(a.doc_date ?? "")),
  );
  const limit = intParam(params, "limit", 50);
  const offset = intParam(params, "offset", 0);
  return {
    total: items.length,
    limit,
    offset,
    sort,
    items: items.slice(offset, offset + limit),
  };
}

function angebotDetail(id: string, d: DemoDataset): Json | undefined {
  return demoAngebote(d).detail[id];
}

function belegDetail(id: string, d: DemoDataset): Json | undefined {
  const recorded = d.belege?.detail?.[id];
  if (recorded !== undefined) {
    const recordedBeleg = recorded.beleg;
    return isJson(recordedBeleg)
      ? { ...recorded, beleg: withBelegReviewCounts(recordedBeleg, d) }
      : recorded;
  }
  const rawBeleg = (d.belege?.items ?? []).find((b) => b.beleg_id === id);
  const beleg = rawBeleg ? withBelegReviewCounts(rawBeleg, d) : undefined;
  if (!beleg) return undefined;
  // Plausible Demo-Positionen aus den Top-Artikeln ableiten.
  const articles = (d.articles?.all ?? []).slice(0, 6);
  const sign = beleg.doc_type === "gutschrift" ? -1 : 1;
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const round1 = (n: number) => Math.round(n * 10) / 10;
  const lines: Json[] = articles.map((a, idx) => {
    const qty = ((idx * 3 + 2) % 9) + 1;
    const anomaly = idx === 0 && num(beleg.open_price_anomaly_count) > 0;
    // Anomalie-Zeile: bewusst teuer eingekauft (Höchstpreis), klar über dem
    // 12-Monats-Schnitt. Schnitt, Delta und Mehrkosten konsistent ableiten.
    const net = anomaly
      ? num(a.worst_net_unit_price) || round2(num(a.last_net_unit_price) * 1.13)
      : num(a.last_net_unit_price);
    // 12-Monats-Schnitt klar unter dem teuren Einkauf, auf ganze Euro gerundet
    // — ergibt eine saubere Story (~13 % über dem Schnitt).
    const avg12m = anomaly ? Math.round(net * 0.88) : num(a.last_net_unit_price);
    const deltaPct = avg12m > 0 ? round1(((net - avg12m) / avg12m) * 100) : 0;
    const overpay = anomaly ? round2((net - avg12m) * qty) : 0;
    const discount = num(a.avg_discount_pct);
    const list = discount > 0 && discount < 100 ? round2(net / (1 - discount / 100)) : net;
    return {
      beleg_id: id,
      line_pos: idx + 1,
      description: a.description_example,
      page_no: 1 + Math.floor(idx / 4),
      article_group_key: a.article_group_key,
      primary_article_code: a.primary_article_code,
      article_code: a.primary_article_code,
      alt_article_code: null,
      manufacturer_article_code: null,
      qty: qty * sign,
      uom: "STK",
      list_unit_price: list,
      discount_pct: discount,
      net_unit_price: net,
      effective_unit_price: net,
      source_line_amount: round2(net * qty * sign),
      signed_line_amount: round2(net * qty * sign),
      project_key: a.last_project_key,
      delivery_address_key: null,
      delivery_address_display: null,
      customer_reference: null,
      context_order_number: null,
      matched_order_doc_ids: [],
      baseline_avg_price: avg12m,
      avg_net_unit_price_12m: avg12m,
      delta_pct_12m: deltaPct,
      canonical_baseline_kind: "12m",
      canonical_baseline_price: avg12m,
      canonical_delta_pct: deltaPct,
      canonical_overpay_abs: overpay,
      overpay_abs: overpay,
      is_price_anomaly: anomaly,
      price_anomaly_status: anomaly ? "open" : null,
      price_anomaly_comment: null,
      price_anomaly_action: null,
      price_anomaly_reviewed_at: null,
      has_cost_center_context: true,
      pdf_available: Boolean(beleg.pdf_available),
      source_file: beleg.source_file,
      source_kind: beleg.source_kind,
    };
  });
  const lineTotal = lines.reduce((acc, l) => acc + num(l.signed_line_amount), 0);
  return {
    beleg,
    matched_orders: [],
    line_total: lineTotal,
    lines,
    review_events: [],
    credit_links: [],
  };
}

function belegComparator(sort: string): (a: Json, b: Json) => number {
  switch (sort) {
    case "amount_desc":
      return (a, b) => num(b.total_amount) - num(a.total_amount);
    case "anomalies_desc":
      return (a, b) =>
        num(b.open_price_anomaly_count) - num(a.open_price_anomaly_count);
    case "open_overpay_desc":
      return compareBelegOpenOverpay("desc");
    case "open_overpay_asc":
      return compareBelegOpenOverpay("asc");
    case "supplier_asc":
      return (a, b) => str(a.supplier).localeCompare(str(b.supplier));
    case "recent_desc":
    default:
      return (a, b) => str(b.doc_date).localeCompare(str(a.doc_date));
  }
}

function compareBelegOpenOverpay(
  direction: "asc" | "desc",
): (a: Json, b: Json) => number {
  return (a, b) => {
    const left = positiveNumberOrNull(a.open_overpay_abs);
    const right = positiveNumberOrNull(b.open_overpay_abs);
    if (left === null && right !== null) return 1;
    if (left !== null && right === null) return -1;
    if (left !== null && right !== null && left !== right) {
      return direction === "asc" ? left - right : right - left;
    }
    const dateOrder = str(b.doc_date).localeCompare(str(a.doc_date));
    return dateOrder || str(a.beleg_id).localeCompare(str(b.beleg_id));
  };
}

function positiveNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function belegeSummary(items: Json[]): Json {
  return {
    total_amount: sum(items, "total_amount"),
    line_count: sum(items, "line_count"),
    price_anomaly_count: sum(items, "price_anomaly_count"),
    open_price_anomaly_count: sum(items, "open_price_anomaly_count"),
    review_open_count: sum(items, "review_open_count"),
    review_complaint_count: sum(items, "review_complaint_count"),
    review_credit_expected_count: sum(items, "review_credit_expected_count"),
    review_write_off_count: sum(items, "review_write_off_count"),
    review_credit_resolved_count: sum(items, "review_credit_resolved_count"),
    review_unspecified_count: sum(items, "review_unspecified_count"),
    supplier_count: new Set(items.map((i) => str(i.supplier))).size,
    gutschrift_count: items.filter((i) => i.doc_type === "gutschrift").length,
  };
}

const REVIEW_STATE_COUNT_KEYS: Record<string, string> = {
  open: "review_open_count",
  complaint: "review_complaint_count",
  credit_expected: "review_credit_expected_count",
  write_off: "review_write_off_count",
  credit_resolved: "review_credit_resolved_count",
  unspecified: "review_unspecified_count",
};

function withBelegReviewCounts(beleg: Json, d: DemoDataset): Json {
  const fallbackOpen = num(beleg.open_price_anomaly_count);
  return {
    ...beleg,
    open_overpay_abs: demoBelegOpenOverpay(beleg, d),
    review_open_count: beleg.review_open_count ?? fallbackOpen,
    review_complaint_count: beleg.review_complaint_count ?? 0,
    review_credit_expected_count: beleg.review_credit_expected_count ?? 0,
    review_write_off_count: beleg.review_write_off_count ?? 0,
    review_credit_resolved_count: beleg.review_credit_resolved_count ?? 0,
    review_unspecified_count: beleg.review_unspecified_count ?? 0,
    review_comment_count: beleg.review_comment_count ?? 0,
  };
}

function demoBelegOpenOverpay(beleg: Json, d: DemoDataset): number | null {
  if (beleg.doc_type !== "rechnung") return null;
  const explicit = positiveNumberOrNull(beleg.open_overpay_abs);
  if (explicit !== null) return explicit;
  const belegId = String(beleg.beleg_id ?? "");
  const detail = d.belege?.detail?.[belegId];
  const lines = isJson(detail) && Array.isArray(detail.lines) ? detail.lines : [];
  let total = 0;
  let hasQualifyingLine = false;
  for (const rawLine of lines) {
    if (!isJson(rawLine)) continue;
    const amount = positiveNumberOrNull(
      rawLine.canonical_overpay_abs ?? rawLine.overpay_abs,
    );
    if (
      rawLine.is_price_anomaly === true &&
      String(rawLine.price_anomaly_status ?? "open") === "open" &&
      amount !== null
    ) {
      total += amount;
      hasQualifyingLine = true;
    }
  }
  return hasQualifyingLine ? Math.round(total * 100) / 100 : null;
}

function isJson(value: unknown): value is Json {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function articleList(params: URLSearchParams, d: DemoDataset): Json {
  let items = [...(d.articles?.all ?? [])];

  const q = params.get("q")?.trim().toLowerCase();
  if (q) {
    // Every word must match, as on the server; the whole query ranks.
    const words = demoSearchWords(q);
    items = items.flatMap((article) => {
      if (!words.every((word) => articleMatchKind(article, word))) return [];
      return [{ ...article, __demo_match_kind: articleMatchKind(article, q) ?? "description" }];
    });
  }
  items = demoArticleFilters(items, params, d);
  const abc = params.get("abc_class");
  if (abc) items = items.filter((a) => a.abc_class === abc);

  const manufacturerIds = params.getAll("manufacturer_id");
  if (manufacturerIds.length) {
    items = items.filter((a) => manufacturerIds.includes(str(a.manufacturer_id)));
  }
  const suppliers = params.getAll("wholesale_supplier");
  if (suppliers.length) {
    items = items.filter((a) =>
      suppliers.some((s) => str(a.supplier_list).includes(s)),
    );
  }

  const sort = params.get("sort") ?? "spend";
  const months = demoBoughtWithin(params);
  items = items.map((article) => ({ ...article, invoice_document_count_window: demoWindowCount(article, months, d) }));
  items.sort(sort === "frequency" && months
    ? (a, b) => num(b.invoice_document_count_window) - num(a.invoice_document_count_window) || str(b.last_invoice_doc_date).localeCompare(str(a.last_invoice_doc_date))
    : articleComparator(sort));

  const limit = intParam(params, "limit", 50);
  const offset = intParam(params, "offset", 0);
  const withTotal = params.get("with_total") === "true";
  const page = items.slice(offset, offset + limit).map((article) => {
    const { __demo_match_kind: _matchKind, ...publicArticle } = article;
    return publicArticle;
  });
  return {
    total: withTotal ? items.length : null,
    limit,
    offset,
    activity_period: articleActivityPeriod(d),
    items: page,
  };
}

const ARTICLE_MATCH_RANK: Record<string, number> = {
  exact_code: 0,
  exact_alias: 1,
  code_prefix: 2,
  alias_prefix: 3,
  code_contains: 4,
  alias_contains: 5,
  product_family: 6,
  description: 7,
};

function articleAliases(article: Json): string[] {
  const rawAliases =
    article.alias_codes ?? article.article_aliases ?? article.aliases ?? [];
  if (!Array.isArray(rawAliases)) return [];
  return rawAliases
    .map((value) => str(value).trim().toLowerCase())
    .filter(Boolean);
}

function demoSearchWords(q: string): string[] {
  return q.toLowerCase().replace(/["″]/g, " ").split(/\s+/).filter(Boolean);
}

function demoBoughtWithin(params: URLSearchParams): number | null {
  const months = Number(params.get("bought_within_months"));
  return [3, 6, 12].includes(months) ? months : null;
}

/** The demo counts from its own data date so the periods show something. */
function demoMonthsAgo(months: number, d: DemoDataset): string {
  const asOf = str(d.articles?.activity_period?.as_of_date) || new Date().toISOString().slice(0, 10);
  const [year, month, day] = asOf.split("-").map(Number);
  const targetMonth = month - 1 - months;
  const lastDay = new Date(Date.UTC(year, targetMonth + 1, 0)).getUTCDate();
  const date = new Date(Date.UTC(year, targetMonth, Math.min(day, lastDay)));
  return date.toISOString().slice(0, 10);
}

const demoLastPurchase = (article: Json) => str(article.last_invoice_doc_date) || str(article.last_order_doc_date);

function demoArticleCostCenters(article: Json, d: DemoDataset): string[] {
  const known = new Set((d.cost_centers?.items ?? []).map((item) => str(item.cost_center_key)));
  const history = d.articles?.detail?.[str(article.article_group_key)]?.price_history as { items?: Json[] } | undefined;
  // Use every evidenced association in the curated excerpt, not only price extrema.
  const keys = [article.last_project_key, article.best_12m_project_key, article.best_active_project_key, article.worst_project_key,
    ...(history?.items ?? []).flatMap((row) => [row.project_key, row.delivery_address_key])];
  return [...new Set(keys.map(str))].filter((key) => key && known.has(key));
}

function demoArticleSuppliers(article: Json, d: DemoDataset): string[] {
  const history = d.articles?.detail?.[str(article.article_group_key)]?.price_history as { items?: Json[] } | undefined;
  return [...new Set((history?.items ?? [])
    .filter((row) => str(row.doc_type).toLowerCase() === "rechnung")
    .map((row) => str(row.supplier).trim().toUpperCase()).filter(Boolean))];
}

/** Großhändler (bei wem gekauft), Kostenstelle und Zeitraum wie /api/articles. */
function demoArticleFilters(items: Json[], params: URLSearchParams, d: DemoDataset): Json[] {
  const suppliers = params.getAll("supplier").map((value) => value.toUpperCase());
  const costCenters = params.getAll("cost_center");
  const months = demoBoughtWithin(params);
  return items.filter((article) =>
    (!suppliers.length || demoArticleSuppliers(article, d).some((value) => suppliers.includes(value)))
    && (!costCenters.length || demoArticleCostCenters(article, d).some((key) => costCenters.includes(key)))
    && (!months || demoWindowCount(article, months, d) > 0));
}

/** Derive shorter windows only when the curated history proves the complete annual count. */
function demoWindowCount(article: Json, months: number | null, d: DemoDataset): number {
  const annualCount = num(article.invoice_document_count_12m);
  if (!months || months === 12) return annualCount;
  const period = articleActivityPeriod(d);
  const history = d.articles?.detail?.[str(article.article_group_key)]?.price_history as { items?: Json[] } | undefined;
  const invoices = (history?.items ?? []).filter((row) =>
    str(row.doc_type).toLowerCase() === "rechnung" && str(row.invoice_doc_id)
    && str(row.doc_date) >= str(period.rolling_12m_from)
    && str(row.doc_date) <= str(period.as_of_date));
  // A truncated history must not present partial counts as the selected period.
  if (new Set(invoices.map((row) => str(row.invoice_doc_id))).size !== annualCount) {
    throw new ApiError(503, "Der Demo-Auszug enthält keine vollständigen Käufe für diesen Zeitraum.");
  }
  const since = demoMonthsAgo(months, d);
  return new Set(invoices.filter((row) => str(row.doc_date) >= since).map((row) => str(row.invoice_doc_id))).size;
}

function articleFilterOptions(params: URLSearchParams, d: DemoDataset): Json {
  const q = params.get("q")?.trim().toLowerCase();
  let items = [...(d.articles?.all ?? [])];
  if (q) items = items.filter((article) => demoSearchWords(q).every((word) => articleMatchKind(article, word)));
  const months = demoBoughtWithin(params);
  if (months) items = items.filter((article) => demoWindowCount(article, months, d) > 0);
  const count = (values: string[]) => {
    const map = new Map<string, number>();
    values.forEach((value) => map.set(value, (map.get(value) ?? 0) + 1));
    return [...map.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  };
  const manufacturers = count(items.map((article) => str(article.manufacturer_id)).filter(Boolean))
    .map(([id, n]) => ({
      id,
      name: str(((d.manufacturers as { items?: Json[] } | undefined)?.items ?? []).find((row) => row.manufacturer_id === id)?.manufacturer_name)
        || str(items.find((article) => article.manufacturer_id === id)?.brand_name) || id,
      article_count: n,
    }));
  const suppliers = count(items.flatMap((article) => demoArticleSuppliers(article, d))).map(([supplier, n]) => ({ supplier, article_count: n }));
  const centers = new Map((d.cost_centers?.items ?? []).map((item) => [str(item.cost_center_key), item]));
  const ccQ = params.get("cost_center_q")?.trim().toLowerCase();
  const selected = params.getAll("cost_center");
  const all = count(items.flatMap((article) => demoArticleCostCenters(article, d))).map(([key, n]) => {
    const item = centers.get(key) ?? {};
    return { type: str(item.cost_center_type), key, label: str(item.display_label) || key, last_date: str(item.last_observed_date) || null, article_count: n };
  }).sort((a, b) => str(b.last_date).localeCompare(str(a.last_date)));
  const costCenters = all.filter((item) => (!ccQ || item.label.toLowerCase().includes(ccQ)) || selected.includes(item.key)).slice(0, 200);
  return {
    manufacturers,
    suppliers,
    cost_centers: costCenters,
    cost_center_total: all.length,
    last_purchase_date: [...(d.articles?.all ?? [])].map(demoLastPurchase).sort().at(-1) ?? null,
  };
}

function articleMatchKind(article: Json, q: string): string | null {
  const code = str(article.primary_article_code).trim().toLowerCase();
  const aliases = articleAliases(article);
  if (code === q) return "exact_code";
  if (aliases.some((alias) => alias === q)) return "exact_alias";
  if (code.startsWith(q)) return "code_prefix";
  if (aliases.some((alias) => alias.startsWith(q))) return "alias_prefix";
  if (code.includes(q)) return "code_contains";
  if (aliases.some((alias) => alias.includes(q))) return "alias_contains";
  if (str(article.product_family).toLowerCase().includes(q)) {
    return "product_family";
  }
  if (str(article.description_example).toLowerCase().includes(q)
    || str(article.description_example).toLowerCase().replaceAll(" ", "").includes(q)) {
    return "description";
  }
  return null;
}

function articleActivityPeriod(d: DemoDataset): Json {
  const period = d.articles?.activity_period;
  if (
    !period ||
    !str(period.as_of_date) ||
    !str(period.time_zone) ||
    !str(period.rolling_12m_from) ||
    !str(period.ytd_from)
  ) {
    throw new ApiError(
      503,
      "Demo-Datensatz enthält keinen verlässlichen Aktivitätszeitraum.",
    );
  }
  return period;
}

function compareArticleActivity(a: Json, b: Json): number {
  const countDifference =
    num(b.invoice_document_count_12m) - num(a.invoice_document_count_12m);
  if (countDifference !== 0) return countDifference;
  const dateDifference = str(b.last_invoice_doc_date_12m).localeCompare(
    str(a.last_invoice_doc_date_12m),
  );
  if (dateDifference !== 0) return dateDifference;
  return str(a.article_group_key).localeCompare(str(b.article_group_key));
}

function articleComparator(sort: string): (a: Json, b: Json) => number {
  switch (sort) {
    case "qty":
      return (a, b) => num(b.total_qty) - num(a.total_qty);
    case "frequency":
      return compareArticleActivity;
    case "relevance":
      return (a, b) => {
        const matchDifference =
          (ARTICLE_MATCH_RANK[
            str(a.__demo_match_kind || a.match_kind)
          ] ?? Number.MAX_SAFE_INTEGER) -
          (ARTICLE_MATCH_RANK[
            str(b.__demo_match_kind || b.match_kind)
          ] ?? Number.MAX_SAFE_INTEGER);
        return matchDifference || compareArticleActivity(a, b);
      };
    case "spread":
      return (a, b) => num(b.net_price_spread_pct) - num(a.net_price_spread_pct);
    case "spread_12m":
      return (a, b) =>
        num(b.net_price_spread_pct_12m) - num(a.net_price_spread_pct_12m);
    case "spread_24m":
      return (a, b) =>
        num(b.net_price_spread_pct_24m) - num(a.net_price_spread_pct_24m);
    case "last_order":
      return (a, b) =>
        str(b.last_order_doc_date).localeCompare(str(a.last_order_doc_date));
    case "spend":
    default:
      return (a, b) => num(b.total_spend) - num(a.total_spend);
  }
}

function articleSuggest(params: URLSearchParams, d: DemoDataset): Json {
  const q = params.get("q")?.trim().toLowerCase() ?? "";
  const limit = intParam(params, "limit", 10);
  let items = [...(d.articles?.all ?? [])];
  if (q) {
    items = items.flatMap((article) => {
      const matchKind = articleMatchKind(article, q);
      return matchKind ? [{ ...article, match_kind: matchKind }] : [];
    });
  } else {
    items = items.map((article) => ({ ...article, match_kind: null }));
  }
  items.sort(articleComparator(q ? "relevance" : "frequency"));
  return {
    limit,
    activity_period: articleActivityPeriod(d),
    items: items.slice(0, limit).map((a) => ({
      article_group_key: a.article_group_key,
      primary_article_code: a.primary_article_code,
      product_family: a.product_family,
      brand_name: a.brand_name,
      last_net_unit_price: a.last_net_unit_price,
      total_spend: a.total_spend,
      description_example: a.description_example,
      invoice_document_count_12m: a.invoice_document_count_12m,
      last_invoice_doc_date_12m: a.last_invoice_doc_date_12m,
      qty_ytd: a.qty_ytd,
      qty_ytd_uom: a.qty_ytd_uom,
      qty_ytd_status: a.qty_ytd_status,
      match_kind: a.match_kind,
    })),
  };
}

// Backend-Resolve-Normalisierung gespiegelt: nur A-Z0-9, groß.
function normalizeDemoCode(code: string): string {
  return code.toUpperCase().replace(/[^A-Z0-9]+/g, "");
}

function resolveCandidate(a: Json): Json {
  return {
    article_group_key: a.article_group_key,
    primary_article_code: a.primary_article_code ?? null,
    description_example: a.description_example ?? null,
    supplier_list: a.supplier_list ?? null,
    last_net_unit_price: a.last_net_unit_price ?? null,
  };
}

function articleResolve(body: Json | undefined, d: DemoDataset): Json {
  const codes = Array.isArray(body?.codes)
    ? (body?.codes as unknown[]).filter(
        (code): code is string => typeof code === "string",
      )
    : [];
  const all = d.articles?.all ?? [];
  return {
    items: codes.map((raw) => {
      const trimmed = raw.trim();
      const norm = normalizeDemoCode(trimmed);
      const matches = norm
        ? all.filter(
            (a) =>
              normalizeDemoCode(String(a.primary_article_code ?? "")) ===
                norm || a.article_group_key === trimmed,
          )
        : [];
      const item: Json = {
        input: trimmed,
        normalized_code: norm || null,
        candidates: matches.slice(0, 5).map(resolveCandidate),
      };
      if (matches.length === 1) {
        item.status = "matched";
        item.article_group_key = matches[0].article_group_key;
      } else if (matches.length > 1) {
        item.status = "ambiguous";
        item.article_group_key = null;
      } else {
        item.status = "unmatched";
        item.article_group_key = null;
      }
      return item;
    }),
  };
}

const DEMO_REFERENCE_WINDOWS: Record<string, number> = {
  best_3m: 3,
  best_6m: 6,
  best_12m: 12,
};

function articleReferencePrices(body: Json | undefined, d: DemoDataset): Json {
  const basis = typeof body?.basis === "string" ? body.basis : "best_12m";
  const windowMonths = DEMO_REFERENCE_WINDOWS[basis];
  if (windowMonths === undefined) {
    throw new ApiError(400, `Demo: unbekannte Referenzbasis ${basis}.`);
  }
  const keys = Array.isArray(body?.article_group_keys)
    ? (body?.article_group_keys as unknown[]).filter(
        (key): key is string => typeof key === "string" && key.length > 0,
      )
    : [];
  const all = d.articles?.all ?? [];
  const cutoffDate = new Date();
  cutoffDate.setMonth(cutoffDate.getMonth() - windowMonths);
  const cutoff = cutoffDate.toISOString().slice(0, 10);

  const items: Json[] = [];
  const missing: string[] = [];
  for (const key of [...new Set(keys)]) {
    const article = all.find((a) => a.article_group_key === key);
    if (!article) {
      missing.push(key);
      continue;
    }
    const detail = d.articles?.detail?.[key] as
      | { price_history?: { items?: Json[] } }
      | undefined;
    const history = detail?.price_history?.items;
    let reference: Json | null = null;
    let windowCount = 0;
    if (Array.isArray(history)) {
      const inWindow = history.filter(
        (row) =>
          num(row.net_unit_price) > 0 &&
          String(row.doc_date ?? "") >= cutoff,
      );
      windowCount = inWindow.length;
      for (const row of inWindow) {
        if (
          reference === null ||
          num(row.net_unit_price) < num(reference.net_unit_price)
        ) {
          reference = row;
        }
      }
    } else {
      // Ohne Detail-Historie nähert die Demo alle Fenster mit den
      // 12-Monats-Rollupwerten des Datensatzes an (echte, nur gröbere Werte).
      windowCount = num(article.priced_line_count_12m);
      if (article.min_net_unit_price_12m != null) {
        reference = {
          net_unit_price: article.min_net_unit_price_12m,
          doc_date: article.best_12m_doc_date ?? null,
          invoice_doc_id: article.best_12m_invoice_doc_id ?? null,
          doc_number: null,
          supplier: null,
          source_kind: null,
        };
      }
    }
    items.push({
      article_group_key: key,
      primary_article_code: article.primary_article_code ?? null,
      description_example: article.description_example ?? null,
      product_family: article.product_family ?? null,
      brand_name: article.brand_name ?? null,
      supplier_list: article.supplier_list ?? null,
      last_net_unit_price: article.last_net_unit_price ?? null,
      last_invoice_doc_date: article.last_invoice_doc_date ?? null,
      reference_price: reference?.net_unit_price ?? null,
      reference_doc_date: reference?.doc_date ?? null,
      reference_invoice_doc_id: reference?.invoice_doc_id ?? null,
      reference_doc_number: reference?.doc_number ?? null,
      reference_supplier: reference?.supplier ?? null,
      reference_source_kind: reference?.source_kind ?? null,
      priced_line_count_window: reference === null ? 0 : windowCount,
    });
  }
  return {
    basis,
    window_months: windowMonths,
    items,
    missing_article_group_keys: missing,
  };
}

function articleDetail(key: string, sub: string, d: DemoDataset): unknown {
  const detail = d.articles?.detail?.[key] as
    | Record<string, Json>
    | undefined;
  const recorded = detail?.[demoSubKey(sub)];
  if (recorded !== undefined) return recorded;
  return articleDetailDefault(sub, key, d);
}

function articleFromList(key: string, d: DemoDataset): Json | undefined {
  return (d.articles?.all ?? []).find((a) => a.article_group_key === key);
}

// Map URL-Subpfad → Bundle-Key.
function demoSubKey(sub: string): string {
  switch (sub) {
    case "price-history":
      return "price_history";
    case "source-coverage":
      return "source_coverage";
    case "qty-monthly":
      return "qty_monthly";
    case "delivery-breakdown":
      return "delivery_breakdown";
    case "top-users":
      return "top_users";
    default:
      return sub; // article, catalog, wholesale, identity, sets, alternatives
  }
}

function articleDetailDefault(sub: string, key: string, d: DemoDataset): unknown {
  switch (sub) {
    case "article":
      // Fällt auf den Listeneintrag zurück, damit jede Artikel-Detailseite lädt.
      return articleFromList(key, d);
    case "catalog": {
      const a = articleFromList(key, d);
      return {
        article_group_key: key,
        primary_article_code: a?.primary_article_code ?? null,
        manufacturer_name: a?.manufacturer_name ?? null,
        ean: null,
        datanorm_manufacturer_article_code: null,
        datanorm_list_price: null,
        datanorm_currency: null,
        datanorm_uom: null,
        warengruppe: null,
        warengruppe_name: a?.product_family ?? null,
        warengruppe_level: null,
        warengruppe_parent_code: null,
        warengruppe_root_code: null,
        warengruppe_breadcrumb: null,
        datanorm_discount_group: null,
        datanorm_supplier: null,
        datanorm_valid_from: null,
        metadata_source: "pdf",
      };
    }
    case "source-coverage": {
      const a = articleFromList(key, d);
      return {
        customer_id: "demo",
        domain: "article",
        entity_key: key,
        entity_type: "article",
        article_group_key: key,
        display_label: a?.description_example ?? null,
        primary_article_code: a?.primary_article_code ?? null,
        manufacturer_name: a?.manufacturer_name ?? null,
        total_spend: a?.total_spend ?? null,
        coverage_status: "complete",
        gap_codes: null,
        unsupported_source_codes: null,
        confidence_label: null,
        has_pdf_evidence: a?.has_pdf_evidence ?? true,
        has_ugl_evidence: a?.has_ugl_evidence ?? false,
        has_datanorm_evidence: a?.has_datanorm_evidence ?? false,
        has_datanorm_catalog: false,
        has_datanorm_set: false,
        has_identity_evidence: a?.has_identity_evidence ?? false,
        has_review_evidence: false,
        has_manual_evidence: false,
        pdf_document_count: a?.document_count ?? 0,
        pdf_line_count: a?.priced_line_count ?? 0,
        ugl_document_count: 0,
        ugl_line_count: 0,
        datanorm_catalog_count: 0,
        datanorm_set_count: 0,
        identity_count: 0,
        weak_identity_count: 0,
        review_candidate_count: 0,
        manual_decision_count: 0,
        supplier_count: a?.distinct_suppliers_count ?? 0,
        suppliers_with_data: a?.supplier_list ?? null,
        suppliers_with_datanorm: null,
        suppliers_with_ugl: null,
        total_observation_count: a?.priced_line_count ?? 0,
        total_ugl_observation_count: 0,
        last_observed_date: a?.last_order_doc_date ?? null,
        supplier_coverage: [],
      };
    }
    case "price-history":
      return { article_group_key: key, total: 0, limit: 0, offset: 0, items: [] };
    case "credits": {
      // The curated demo article carries one sample Gutschrift so the orders
      // list demonstrates credit-note handling; other articles have none.
      const a = articleFromList(key, d);
      if (!a || key !== "art-001") {
        return { article_group_key: key, total: 0, limit: 0, offset: 0, items: [] };
      }
      const net = num(a.last_net_unit_price);
      const qty = 2;
      return {
        article_group_key: key,
        total: 1,
        limit: 200,
        offset: 0,
        items: [
          {
            doc_date: "2026-05-09",
            doc_type: "gutschrift",
            source_kind: "pdf_gutschrift",
            doc_number: "GU-2026-0042",
            line_pos: "1",
            project_key: a.last_project_key ?? null,
            project_exists: a.last_project_key != null,
            supplier: "SHT",
            qty: -qty,
            uom: "STK",
            list_unit_price: null,
            discount_pct: null,
            net_unit_price: net,
            effective_unit_price: net,
            line_net_amount: Math.round(-net * qty * 100) / 100,
            baseline_kind: null,
            result: null,
            description: a.description_example ?? null,
            page_no: 1,
            invoice_doc_id: "gutschrift:demo-0042",
            online_user_email: null,
            online_user_name: null,
            delivery_address_key: null,
            delivery_address_display: null,
            delivery_is_warehouse: null,
          },
        ],
      };
    }
    case "wholesale":
      return {
        article_group_key: key,
        supplier_filter: null,
        available_suppliers: [],
        supplier_rollup: [],
        list_price: null,
        evidence: [],
        evidence_total: 0,
      };
    case "identity":
      return { article_group_key: key, identities: [], equivalents: [] };
    case "sets":
      return {
        article_group_key: key,
        member_of: [],
        contains_members: [],
        qa: [],
      };
    case "qty-monthly":
      return { article_group_key: key, months: 0, items: [] };
    case "delivery-breakdown":
      return { article_group_key: key, items: [] };
    case "alternatives":
      return {
        article_group_key: key,
        manufacturer_id: null,
        product_family: null,
        items: [],
      };
    case "top-users":
      return { article_group_key: key, items: [] };
    default:
      return undefined; // führt zu 404 → react-query Fehlerzustand
  }
}

function costCenterList(params: URLSearchParams, d: DemoDataset): Json {
  let items = [...(d.cost_centers?.items ?? [])];
  const type = params.get("type");
  if (type) items = items.filter((c) => c.cost_center_type === type);
  const q = params.get("q")?.trim().toLowerCase();
  if (q) {
    items = items.filter((c) => str(c.display_label).toLowerCase().includes(q));
  }
  const sort = params.get("sort");
  items.sort(costCenterComparator(sort));
  const limit = intParam(params, "limit", 500);
  const offset = intParam(params, "offset", 0);
  const page = items.slice(offset, offset + limit);
  return {
    total: items.length,
    limit,
    offset,
    summary: costCenterSummary(items),
    items: page,
  };
}

function costCenterComparator(sort: string | null): (a: Json, b: Json) => number {
  switch (sort) {
    case "spend_last_year_desc":
      return (a, b) => num(b.spend_last_year) - num(a.spend_last_year);
    case "recent_desc":
      return (a, b) =>
        str(b.last_observed_date).localeCompare(str(a.last_observed_date));
    case "spend_desc":
    default:
      return (a, b) => num(b.total_spend) - num(a.total_spend);
  }
}

function costCenterSummary(items: Json[]): Json {
  return {
    entity_count: items.length,
    total_spend: sum(items, "total_spend"),
    spend_ytd: sum(items, "spend_ytd"),
    spend_last_year: sum(items, "spend_last_year"),
    spend_last30: sum(items, "spend_last30"),
    document_count: sum(items, "document_count"),
    line_count: sum(items, "line_count"),
    user_count: sum(items, "user_count"),
    with_pdf_evidence: items.filter((i) => i.has_pdf_evidence).length,
    with_ugl_evidence: items.filter((i) => i.has_ugl_evidence).length,
    with_manual_evidence: items.filter((i) => i.has_manual_evidence).length,
    project_count: items.filter((i) => i.cost_center_type === "project").length,
    delivery_address_count: items.filter(
      (i) => i.cost_center_type === "delivery_address",
    ).length,
    customer_reference_count: items.filter(
      (i) => i.cost_center_type === "customer_reference",
    ).length,
    order_context_count: items.filter(
      (i) => i.cost_center_type === "order_context",
    ).length,
    manual_cost_center_count: items.filter(
      (i) => i.cost_center_type === "manual_cost_center",
    ).length,
  };
}

function costCenterCoverage(params: URLSearchParams, d: DemoDataset): Json {
  const base = d.cost_centers?.source_coverage as Json | undefined;
  const allItems = (base?.items as Json[] | undefined) ?? [];
  if (!allItems.length) return base ?? emptyCoverage();

  const type = params.get("type");
  const q = params.get("q")?.trim().toLowerCase();
  let items = type
    ? allItems.filter((r) => r.entity_type === type)
    : [...allItems];
  if (q) {
    items = items.filter((r) => str(r.display_label).toLowerCase().includes(q));
  }
  const limit = intParam(params, "limit", 500);
  const offset = intParam(params, "offset", 0);
  const page = items.slice(offset, offset + limit);
  return {
    total: items.length,
    limit,
    offset,
    summary: coverageSummary(items),
    items: page,
  };
}

function costCenterDetail(params: URLSearchParams, d: DemoDataset): Json | undefined {
  const key = params.get("key");
  if (!key) return undefined;
  return (d.cost_centers?.items ?? []).find((c) => c.cost_center_key === key);
}

function costCenterCoverageDetail(
  params: URLSearchParams,
  d: DemoDataset,
): Json | undefined {
  const key = params.get("key");
  if (!key) return undefined;
  const items = (d.cost_centers?.source_coverage as Json | undefined)?.items as
    | Json[]
    | undefined;
  const recorded = (items ?? []).find((r) => r.entity_key === key);
  if (recorded) return recorded;
  // Fallback aus dem Entitäts-Eintrag, damit die Detailseite lädt.
  const entity = (d.cost_centers?.items ?? []).find(
    (c) => c.cost_center_key === key,
  );
  if (!entity) return undefined;
  return {
    customer_id: "demo",
    domain: "cost_center",
    row_kind: "cost_center",
    entity_key: key,
    entity_type: entity.cost_center_type,
    display_label: entity.display_label,
    total_spend: entity.total_spend,
    spend_ytd: entity.spend_ytd,
    spend_last_year: entity.spend_last_year,
    spend_last30: entity.spend_last30,
    source_observation_count: entity.source_observation_count,
    line_observation_count: entity.line_observation_count,
    document_count: entity.document_count,
    line_count: entity.line_count,
    user_count: entity.user_count,
    last_observed_date: entity.last_observed_date,
    has_pdf_evidence: entity.has_pdf_evidence,
    has_ugl_evidence: entity.has_ugl_evidence,
    has_datanorm_evidence: false,
    has_manual_evidence: entity.has_manual_evidence,
    pdf_document_count: entity.document_count,
    pdf_line_count: entity.line_count,
    ugl_document_count: 0,
    ugl_line_count: 0,
    datanorm_catalog_count: 0,
    datanorm_set_count: 0,
    manual_decision_count: 0,
    matched_link_count: 0,
    relationship_candidate_count: 0,
    open_relationship_count: 0,
    confirmed_relationship_count: 0,
    rejected_relationship_count: 0,
    coverage_status: "complete",
    gap_codes: null,
    unsupported_source_codes: null,
    confidence_label: entity.confidence_labels,
  };
}

function costCenterSpendByMonth(key: string, d: DemoDataset): Json {
  const entity = (d.cost_centers?.items ?? []).find(
    (c) => c.cost_center_key === key,
  );
  const yearly = num(entity?.spend_last_year) || 60000;
  const months = 12;
  const base = yearly / months;
  const items: Json[] = [];
  let cumulativeSpend = 0;
  let cumulativeLines = 0;
  for (let i = 0; i < months; i += 1) {
    const date = new Date(Date.UTC(2025, 5 + i, 1));
    const factor = 0.7 + ((i * 7) % 11) / 18; // 0.7–1.3
    const spend = Math.round(base * factor);
    const lines = Math.max(1, Math.round(spend / 90));
    cumulativeSpend += spend;
    cumulativeLines += lines;
    items.push({
      month_start: date.toISOString().slice(0, 10),
      total_spend: spend,
      line_count: lines,
      avg_discount_pct: 34 + ((i * 3) % 9),
      distinct_articles: Math.max(4, Math.round(lines / 4)),
      cumulative_spend: cumulativeSpend,
      cumulative_line_count: cumulativeLines,
    });
  }
  return { items };
}

function coverageSummary(items: Json[]): Json {
  const byStatus = (status: string) =>
    items.filter((i) => i.coverage_status === status).length;
  return {
    total: items.length,
    complete: byStatus("complete"),
    review_needed: byStatus("review_needed"),
    source_gap: byStatus("source_gap"),
    unsupported: byStatus("unsupported"),
    unknown: byStatus("unknown"),
    with_pdf_evidence: items.filter((i) => i.has_pdf_evidence).length,
    with_ugl_evidence: items.filter((i) => i.has_ugl_evidence).length,
    with_datanorm_evidence: items.filter((i) => i.has_datanorm_evidence).length,
    with_manual_evidence: items.filter((i) => i.has_manual_evidence).length,
    total_spend: sum(items, "total_spend"),
    spend_ytd: sum(items, "spend_ytd"),
    spend_last_year: sum(items, "spend_last_year"),
    spend_last30: sum(items, "spend_last30"),
    document_count: sum(items, "document_count"),
    line_count: sum(items, "line_count"),
  };
}

function emptyCoverage(): Json {
  return {
    total: 0,
    limit: 0,
    offset: 0,
    summary: {
      total: 0,
      complete: 0,
      review_needed: 0,
      source_gap: 0,
      unsupported: 0,
      unknown: 0,
      with_pdf_evidence: 0,
      with_ugl_evidence: 0,
      with_datanorm_evidence: 0,
      with_manual_evidence: 0,
    },
    items: [],
  };
}

function projectList(params: URLSearchParams, d: DemoDataset): Json {
  let items = [...(d.projects?.items ?? [])];
  if (params.get("active_only") === "true") {
    items = items.filter((p) => p.active !== false);
  }
  const q = params.get("q")?.trim().toLowerCase();
  if (q) {
    items = items.filter((p) =>
      [p.display_name, p.project_code, p.customer_raw].some((v) =>
        str(v).toLowerCase().includes(q),
      ),
    );
  }
  const limit = intParam(params, "limit", 100);
  const offset = intParam(params, "offset", 0);
  return {
    total: items.length,
    limit,
    offset,
    summary: d.projects?.summary,
    items: items.slice(offset, offset + limit),
  };
}

function varianceFlat(params: URLSearchParams, d: DemoDataset): Json {
  if (params.get("format") === "summary") {
    return Object.fromEntries((["sufficient", "insufficient"] as const).map((quality) => {
      const selection = new URLSearchParams(params);
      selection.set("reference_quality", quality);
      const rows = filteredVarianceItems(selection, d);
      return [quality, { count: rows.length, difference: String(rows.reduce((total, row) => total + Math.max(0, varianceOverpay(row)), 0)), unknown_amount_count: rows.filter((row) => row.canonical_overpay_abs == null && row.overpay_abs == null).length }];
    }));
  }
  const items = filteredVarianceItems(params, d);

  const sort = params.get("sort") ?? "overpay_desc";
  items.sort(varianceComparator(sort));

  const limit = intParam(params, "limit", 100);
  const offset = intParam(params, "offset", 0);
  return {
    total: items.length,
    limit,
    offset,
    sort,
    items: items.slice(offset, offset + limit),
  };
}

function filteredVarianceItems(params: URLSearchParams, d: DemoDataset): Json[] {
  let items = (d.variance?.flat ?? []).map(withDemoPriceAnomalyStatus);

  const from = params.get("from");
  const to = params.get("to");
  if (from) items = items.filter((r) => str(r.doc_date) >= from);
  if (to) items = items.filter((r) => str(r.doc_date) <= to);

  const onlineUser = params.get("online_user");
  if (onlineUser) items = items.filter((r) => r.online_user_email === onlineUser);

  items = filterMulti(items, params, "project", "project_key");
  items = filterMulti(items, params, "article", "article_group_key");
  items = filterMulti(items, params, "manufacturer_id", "manufacturer_id");
  items = filterMulti(items, params, "supplier", "supplier");
  items = filterMulti(items, params, "delivery_address_key", "delivery_address_key");
  const quality = params.get("reference_quality");
  const sufficient = (r: Json) => num(r.canonical_baseline_line_count) >= Math.max(2, num(r.price_anomaly_minimum_reference_count ?? 2)) && num(r.canonical_baseline_document_count) >= 2;
  if (quality) items = items.filter((r) => quality === "sufficient" ? sufficient(r) : num(r.canonical_baseline_line_count) > 0 && !sufficient(r));
  const minReferences = params.get("min_reference_count");
  if (minReferences && quality !== "insufficient") items = items.filter((r) => num(r.canonical_baseline_line_count) >= Number(minReferences));
  const reviewStatus = params.get("price_anomaly_status");
  if (reviewStatus && quality !== "insufficient") {
    items = items.filter((r) => r.price_anomaly_status === reviewStatus);
  }

  const minDelta = params.get("min_delta_pct");
  // Like the backend: positive_only keeps dearer purchases and then applies the
  // threshold upwards only; otherwise the threshold works in both directions.
  if (params.get("positive_only") === "true") {
    items = items.filter((r) => varianceDeltaPct(r) > 0 && (!minDelta || varianceDeltaPct(r) >= Number(minDelta)));
  } else if (minDelta) {
    const threshold = Number(minDelta);
    items = items.filter((r) => Math.abs(varianceDeltaPct(r)) >= threshold);
  }
  const minOverpay = params.get("min_overpay_abs");
  if (minOverpay) {
    const threshold = Number(minOverpay);
    items = items.filter((r) => varianceOverpay(r) >= threshold);
  }
  const isWarehouse = params.get("is_warehouse");
  if (isWarehouse != null) {
    const want = isWarehouse === "true";
    items = items.filter((r) => Boolean(r.delivery_is_warehouse) === want);
  }
  return items;
}

function varianceComparator(sort: string): (a: Json, b: Json) => number {
  switch (sort) {
    case "overpay_asc":
      return (a, b) => varianceOverpay(a) - varianceOverpay(b);
    case "delta_pct_desc":
      return (a, b) => varianceDeltaPct(b) - varianceDeltaPct(a);
    case "delta_pct_asc":
      return (a, b) => varianceDeltaPct(a) - varianceDeltaPct(b);
    case "doc_date_desc":
      return (a, b) => str(b.doc_date).localeCompare(str(a.doc_date));
    case "doc_date_asc":
      return (a, b) => str(a.doc_date).localeCompare(str(b.doc_date));
    case "net_price_desc":
      return (a, b) => num(b.net_unit_price) - num(a.net_unit_price);
    case "net_price_asc":
      return (a, b) => num(a.net_unit_price) - num(b.net_unit_price);
    case "overpay_desc":
    default:
      return (a, b) => varianceOverpay(b) - varianceOverpay(a);
  }
}

function varianceGrouped(params: URLSearchParams, d: DemoDataset): Json {
  const groupBy = params.get("group_by") ?? "project";
  const reviewStatus = params.get("price_anomaly_status");
  if (reviewStatus || params.has("reference_quality") || params.has("positive_only") || params.has("from")) {
    const axes = groupBy.split(",");
    const items = groupVarianceItems(filteredVarianceItems(params, d), axes);
    items.sort(groupedVarianceComparator(params.get("sort") ?? "overpay_desc"));
    return { group_by: axes, items };
  }

  const recorded = d.variance?.grouped?.[groupBy];
  if (recorded !== undefined) return recorded;
  return { group_by: groupBy.split(","), items: [] };
}

/**
 * Einkaufsvolumen je Monat aus denselben Demo-Positionen wie die Mehrkosten,
 * damit beide Reiter zusammenpassen (gleiche Regel wie das Backend).
 */
function purchasesMonthly(params: URLSearchParams, d: DemoDataset): Json {
  let items = d.variance?.flat ?? [];
  const from = params.get("from");
  const to = params.get("to");
  if (from) items = items.filter((r) => str(r.doc_date) >= from);
  if (to) items = items.filter((r) => str(r.doc_date) <= to);
  items = filterMulti(items, params, "supplier", "supplier");
  items = filterMulti(items, params, "manufacturer_id", "manufacturer_id");
  const groupBy = params.get("group_by");
  const quality = params.get("reference_quality");
  const sufficient = (r: Json) => num(r.canonical_baseline_line_count) >= Math.max(2, num(r.price_anomaly_minimum_reference_count ?? 2)) && num(r.canonical_baseline_document_count) >= 2;
  const referenceMatches = (r: Json) => !quality || (quality === "sufficient" ? sufficient(r) : num(r.canonical_baseline_line_count) > 0 && !sufficient(r));
  const groups = new Map<string, Json & { docs: Set<string>; articles: Set<string> }>();
  for (const item of items) {
    const month = str(item.doc_date).slice(0, 7);
    if (!month) continue;
    const axis = groupBy === "manufacturer" ? str(item.manufacturer_id) : groupBy === "supplier" ? str(item.supplier) : "";
    const key = `${month}\u001f${axis}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        month,
        ...(groupBy === "manufacturer" ? { manufacturer_id: item.manufacturer_id ?? null, manufacturer_name: item.manufacturer_name ?? null } : {}),
        ...(groupBy === "supplier" ? { supplier: item.supplier ?? null } : {}),
        line_count: 0, total_spend: 0, total_overpay: 0, docs: new Set<string>(), articles: new Set<string>(),
      };
      groups.set(key, group);
    }
    group.line_count = num(group.line_count) + 1;
    group.total_spend = num(group.total_spend) + num(item.line_net_amount);
    if (varianceDeltaPct(item) > 0 && referenceMatches(item)) group.total_overpay = num(group.total_overpay) + Math.max(0, varianceOverpay(item));
    group.docs.add(str(item.invoice_doc_id));
    group.articles.add(str(item.article_group_key));
  }
  const rows: Json[] = Array.from(groups.values(), ({ docs, articles, ...group }) => ({ ...group, document_count: docs.size, article_count: articles.size }) as Json);
  rows.sort((a, b) => str(a.month).localeCompare(str(b.month)));
  const dataUntil = (d.variance?.flat ?? []).reduce((latest, row) => (str(row.doc_date) > latest ? str(row.doc_date) : latest), "");
  return { group_by: groupBy, data_until: dataUntil || null, items: rows };
}

function withDemoPriceAnomalyStatus(row: Json): Json {
  return {
    ...row,
    price_anomaly_rule_revision: row.price_anomaly_rule_revision ?? 0,
    price_anomaly_comparison_basis:
      row.price_anomaly_comparison_basis ?? "prior_12m_fallback",
    price_anomaly_threshold_pct: row.price_anomaly_threshold_pct ?? "15.000",
    price_anomaly_minimum_overpay_abs:
      row.price_anomaly_minimum_overpay_abs ?? null,
    price_anomaly_status: demoPriceAnomalyStatus(row),
  };
}

function demoPriceAnomalyStatus(row: Json): string | null {
  const explicitStatus = row.price_anomaly_status;
  if (typeof explicitStatus === "string" && explicitStatus.length > 0) {
    return explicitStatus;
  }
  if (row.is_price_anomaly === true) return "open";
  if (row.is_price_anomaly === false) return null;
  if ("price_anomaly_status" in row || "is_price_anomaly" in row) return null;
  return varianceDeltaPct(row) >= PRICE_ANOMALY_DELTA_THRESHOLD ? "open" : null;
}

function varianceDeltaPct(row: Json): number {
  return num(row.canonical_delta_pct ?? row.delta_pct ?? row.delta_pct_12m);
}

function varianceOverpay(row: Json): number {
  return num(row.canonical_overpay_abs ?? row.overpay_abs);
}

function demoPriceAnomalyRule(): Json {
  const rule = {
    revision: 0,
    comparison_basis: "prior_12m_fallback",
    threshold_pct: "15",
    minimum_overpay_abs: null,
    minimum_reference_count: 1,
    updated_at: null,
  };
  return {
    configured: rule,
    effective: rule,
    reference_counts_available: false,
    refresh_pending: false,
    can_manage: false,
    activation: "next_regular_refresh",
  };
}

function groupVarianceItems(items: Json[], axes: string[]): Json[] {
  type Group = Json & {
    line_count: number;
    total_qty: number;
    total_spend: number;
    total_overpay: number;
    overpay_line_count: number;
    avg_delta_pct: number;
    first_doc_date: string | null;
    last_doc_date: string | null;
    delta_sum: number;
  };
  const groups = new Map<string, Group>();

  for (const item of items) {
    const key = axes.map((axis) => str(item[groupAxisKey(axis)])).join("\u001f");
    let group = groups.get(key);
    if (!group) {
      group = groupSeed(item, axes);
      groups.set(key, group);
    }
    group.line_count += 1;
    group.total_qty += num(item.qty);
    group.total_spend += num(item.line_net_amount);
    const deltaPct = varianceDeltaPct(item);
    if (deltaPct > 0) group.total_overpay += Math.max(0, varianceOverpay(item));
    group.overpay_line_count += deltaPct > 0 ? 1 : 0;
    if (deltaPct > 0) group.max_delta_pct = Math.max(num(group.max_delta_pct ?? 0), deltaPct);
    group.delta_sum += deltaPct;
    group.avg_delta_pct = group.delta_sum / group.line_count;
    const docDate = str(item.doc_date);
    if (docDate) {
      group.first_doc_date =
        group.first_doc_date && group.first_doc_date < docDate
          ? group.first_doc_date
          : docDate;
      group.last_doc_date =
        group.last_doc_date && group.last_doc_date > docDate
          ? group.last_doc_date
          : docDate;
    }
  }

  return Array.from(groups.values(), ({ delta_sum: _deltaSum, ...group }) => group);
}

function groupSeed(item: Json, axes: string[]): Json & {
  line_count: number;
  total_qty: number;
  total_spend: number;
  total_overpay: number;
  overpay_line_count: number;
  avg_delta_pct: number;
  first_doc_date: string | null;
  last_doc_date: string | null;
  delta_sum: number;
} {
  const group: Json & {
    line_count: number;
    total_qty: number;
    total_spend: number;
    total_overpay: number;
    overpay_line_count: number;
    avg_delta_pct: number;
    first_doc_date: string | null;
    last_doc_date: string | null;
    delta_sum: number;
  } = {
    line_count: 0,
    total_qty: 0,
    total_spend: 0,
    total_overpay: 0,
    overpay_line_count: 0,
    avg_delta_pct: 0,
    first_doc_date: null,
    last_doc_date: null,
    delta_sum: 0,
  };

  if (axes.includes("project")) group.project_key = item.project_key ?? null;
  if (axes.includes("delivery_address")) {
    group.delivery_address_key = item.delivery_address_key ?? null;
    group.delivery_address_display = item.delivery_address_display ?? null;
    group.delivery_is_warehouse = Boolean(item.delivery_is_warehouse);
  }
  if (axes.includes("manufacturer")) {
    group.manufacturer_id = item.manufacturer_id ?? null;
    group.manufacturer_name = item.manufacturer_name ?? null;
  }
  if (axes.includes("article")) {
    group.article_group_key = item.article_group_key ?? null;
    group.primary_article_code = item.primary_article_code ?? null;
    // Like the backend (#543): the article is named, not only numbered.
    group.description = item.description ?? null;
    group.brand_name = item.brand_name ?? null;
    group.product_family = item.product_family ?? null;
    if (!axes.includes("manufacturer")) {
      group.manufacturer_id = item.manufacturer_id ?? null;
      group.manufacturer_name = item.manufacturer_name ?? null;
    }
  }
  if (axes.includes("supplier")) group.supplier = item.supplier ?? null;
  return group;
}

function groupAxisKey(axis: string): string {
  switch (axis) {
    case "delivery_address":
      return "delivery_address_key";
    case "manufacturer":
      return "manufacturer_id";
    case "article":
      return "article_group_key";
    case "supplier":
      return "supplier";
    case "project":
    default:
      return "project_key";
  }
}

function groupedVarianceComparator(sort: string): (a: Json, b: Json) => number {
  switch (sort) {
    case "spend_desc":
      return (a, b) => num(b.total_spend) - num(a.total_spend);
    case "lines_desc":
      return (a, b) => num(b.line_count) - num(a.line_count);
    case "max_delta_desc":
      return (a, b) => num(b.max_delta_pct ?? -Infinity) - num(a.max_delta_pct ?? -Infinity) || num(b.total_overpay) - num(a.total_overpay);
    case "overpay_desc":
    default:
      return (a, b) => num(b.total_overpay) - num(a.total_overpay);
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function matchPath(
  path: string,
  pattern: string,
): Record<string, string> | null {
  const pSeg = pattern.split("/");
  const aSeg = path.split("/");
  if (pSeg.length !== aSeg.length) return null;
  const out: Record<string, string> = {};
  for (let i = 0; i < pSeg.length; i += 1) {
    if (pSeg[i].startsWith(":")) {
      out[pSeg[i].slice(1)] = decodeURIComponent(aSeg[i]);
    } else if (pSeg[i] !== aSeg[i]) {
      return null;
    }
  }
  return out;
}

function filterMulti(
  items: Json[],
  params: URLSearchParams,
  param: string,
  field: string,
): Json[] {
  const values = params.getAll(param);
  if (!values.length) return items;
  return items.filter((r) => values.includes(str(r[field])));
}

function intParam(params: URLSearchParams, key: string, fallback: number): number {
  const raw = params.get(key);
  if (raw == null) return fallback;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function num(value: unknown): number {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function str(value: unknown): string {
  return value == null ? "" : String(value);
}

function sum(items: Json[], field: string): number {
  return items.reduce((acc, item) => acc + num(item[field]), 0);
}
